<script lang="ts" setup>
  import { format, parse } from 'date-fns'

  import type { FiltersParams } from '~/composables/useFilters'
  import { DATE_FORMAT_LITE } from '~/core/constants'
  import { LocationType, ResultsCategory, type ResultsLocation } from '~/core/types'
  import { BngContentFit, BngModal } from '~/layouts/components'
  import { BNGPartners } from '~/partner.type'

  const props = defineProps<{ collapsed: boolean }>()

  const INITIAL_RESULTS_LOCATION: ResultsLocation = {
    type: LocationType.COUNTRY,
    value: {
      label: 'Toute la France',
      code: 'fr',
    },
  }

  const { t } = useI18n()
  const router = useRouter()
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)
  const { groups, noResult, debouncedHandleLocationInputSearch, partner, activeSearchValue } = useSearch()

  const searchStore = useSearchStore()
  const { displayToast } = useUiStore()

  const { lastLocationResults, lastResultsItem } = storeToRefs(searchStore)

  const resultsLocation = ref<ResultsLocation>(INITIAL_RESULTS_LOCATION)
  const resultsCategory = ref<ResultsCategory>(ResultsCategory.GREEN_FEE)
  const activeDate = ref(format(new Date(), DATE_FORMAT_LITE))
  const loadingLocation = ref(false)
  const dateModal = ref(false)
  const locationModal = ref(false)
  const displayLocationInputDropdown = ref(false)
  const displayDateInputDropdown = ref(false)

  const currentDateLabel = computed(() => format(parse(activeDate.value, DATE_FORMAT_LITE, new Date()), 'dd/MM/yyyy'))

  function updateLastResult(location: ResultsLocation) {
    if ([LocationType.PROXIMITY, LocationType.COUNTRY].includes(location.type)) {
      return lastLocationResults.value
    }
    const exist = lastLocationResults.value.find((l) => JSON.stringify(l.value) === JSON.stringify(location.value))
    if (exist) {
      return lastLocationResults.value
    }
    const res = lastLocationResults.value
    res.unshift(location)
    if (res.length > 5) {
      res.pop()
    }
    lastLocationResults.value = res
    return res
  }

  const categories = computed((): Record<string, string> => {
    if (partner.value === BNGPartners.BLUE_GREEN) {
      return {
        [ResultsCategory.GREEN_FEE]: 'products.green-fee',
      }
    }
    return {
      [ResultsCategory.GREEN_FEE]: 'products.green-fee',
      [ResultsCategory.PRODUCTS]: 'products.more-products',
    }
  })

  function handleLocationInputClick() {
    locationModal.value = true
  }

  function closeLocationModal(value: ResultsLocation) {
    locationModal.value = false
    resultsLocation.value = value
  }

  function handleDateInputClick() {
    dateModal.value = true
  }

  function handleDateInputReset() {
    activeDate.value = format(new Date(), DATE_FORMAT_LITE)
  }

  function handleLocationInput(value: ResultsLocation) {
    updateLastResult(value)
    displayLocationInputDropdown.value = false
    resultsLocation.value = value
  }

  function handleCurrentLocationError() {
    displayToast(`Erreur lors de la geolocalisation`, 'error', 2000)
  }

  function handleLocationInputReset() {
    resultsLocation.value = INITIAL_RESULTS_LOCATION
  }

  function handleDateInput(date: typeof activeDate.value) {
    activeDate.value = date
    displayDateInputDropdown.value = false
    dateModal.value = false
  }

  const currentLocationLabel = computed(() => {
    if (!searchValueFieldIsActive.value) {
      return resultsLocationLabel(resultsLocation.value)
    }
    if (activeSearchValue.value === null) {
      return ''
    }
    return activeSearchValue.value
  })

  const searchValueFieldIsActive = ref(false)

  function resetAtClick() {
    if (searchValueFieldIsActive.value) {
      return
    }
    searchValueFieldIsActive.value = true
    activeSearchValue.value = ''
    displayLocationInputDropdown.value = true
    displayDateInputDropdown.value = false
  }

  function handleLocationInputBlur() {
    searchValueFieldIsActive.value = false
  }

  function handleClickOutsideLocationDropdown() {
    activeSearchValue.value = ''
    displayLocationInputDropdown.value = false
  }

  function submit() {
    if (resultsLocation.value.type === LocationType.GOLF) {
      return router.push({ name: 'golf-golfSlug', params: { golfSlug: resultsLocation.value.value.slug }, query: { date: activeDate.value } })
    }
    if (props.collapsed) {
      document.getElementById('layout-observer-root')?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      return
    }
    const params: FiltersParams = {
      locationValue: resultsLocationValue(resultsLocation.value),
      productType: resultsCategory.value ?? ResultsCategory.GREEN_FEE,
      locationType: resultsLocation.value.type,
    }
    const date = format(parse(activeDate.value, DATE_FORMAT_LITE, new Date()), DATE_FORMAT_LITE)
    const query = resultsCategory.value === ResultsCategory.GREEN_FEE ? { ...route.query, date } : route.query
    router.push({
      name: 'search',
      params,
      query,
    })
  }

  watch(activeDate, () => {
    displayDateInputDropdown.value = false
  })

</script>

<template>
  <div
    class="c-search"
    :class="{collapsed: props.collapsed}"
  >
    <ClientOnly>
      <Teleport
        v-if="!matchDesktop && dateModal"
        to="#modal"
      >
        <BngModal
          :hide-backdrop="true"
          closable
          @close="dateModal = false"
        >
          <DatePicker
            :model-value="activeDate"
            @update:model-value="handleDateInput"
          />
        </BngModal>
      </Teleport>
      <Teleport
        v-if="!matchDesktop && locationModal"
        to="#modal"
      >
        <BngModal
          :hide-backdrop="true"
          closable
          @close="locationModal = false"
        >
          <AutocompleteLocation
            :current-location-label="currentLocationLabel"
            :groups="groups"
            @close="closeLocationModal"
          />
        </BngModal>
      </Teleport>
    </ClientOnly>
    <BngContentFit>
      <div class="body">
        <div class="search-header">
          <SvgIcon
            v-show="matchDesktop"
            class="large-icon"
            :height="38"
            :width="38"
            icon="arrow"
          />
          <div class="title">
            <span>{{ t("search.title.base") }}, </span><span class="colored">{{ t("search.title.simple") }} </span>&nbsp;<span>{{ t("search.title.and") }} </span>&nbsp;<span class="colored">{{ t("search.title.fast") }}&nbsp;</span><span>!</span>
          </div>
        </div>
        <div class="fields">
          <div class="categories">
            <div class="title">
              {{ t("common.what") }} ?
            </div>
            <ButtonSelector
              v-if="!runtimeConfig.public.isMobile"
              v-model="resultsCategory"
              :class="{ wrap: matchDesktop }"
              :items="categories"
            />
          </div>
          <ClientOnly>
            <div
              class="where"
            >
              <div class="title">
                {{ t("common.where") }} ?
              </div>
              <InputBase
                v-if="!matchDesktop"
                icon="location"
                :placeholder="`${t('common.where')} ?`"
                name="where"
                :model-value="currentLocationLabel"
                readonly
                :disabled="loadingLocation"
                @click.stop="handleLocationInputClick"
              />
              <InputDropdown
                v-else
                full
                :model-value="currentLocationLabel"
                :dropdown-displayed="displayLocationInputDropdown"
                name="location-dropdown"
                :input-props="{
                  resetable: true,
                  disabled: loadingLocation,
                  icon: 'location',
                  placeholder: `${t('common.where')} ?`,
                  name: 'where',
                }"
                @update:model-value="debouncedHandleLocationInputSearch"
                @reset="handleLocationInputReset"
                @input-click="resetAtClick"
                @click-outside="handleClickOutsideLocationDropdown"
                @blur="handleLocationInputBlur"
              >
                <InputLocation
                  :loading="false"
                  :no-result="noResult"
                  :active-search="!!activeSearchValue"
                  :groups="groups"
                  :last-results="lastResultsItem"
                  @input="handleLocationInput"
                  @current-location-error="handleCurrentLocationError"
                />
              </InputDropdown>
            </div>
          </ClientOnly>
          <ClientOnly>
            <div
              class="when"
              @click.stop="handleDateInputClick"
            >
              <template v-if="resultsCategory === ResultsCategory.GREEN_FEE">
                <div class="title">
                  {{ t("common.when") }} ?
                </div>
                <InputBase
                  v-if="!matchDesktop"
                  icon="calendar"
                  :placeholder="`${t('common.when')} ?`"
                  name="when"
                  :model-value="currentDateLabel"
                  :readonly="true"
                />
                <InputDropdown
                  v-else
                  :model-value="currentDateLabel"
                  :dropdown-displayed="displayDateInputDropdown"
                  name="calendar-dropdown"
                  :input-props="{
                    resetable: true,
                    icon: 'calendar',
                    placeholder: `${t('common.when')} ?`,
                    name: 'when',
                  }"
                  @reset="handleDateInputReset"
                  @input-click="
                    displayDateInputDropdown = true;
                    displayLocationInputDropdown = false;
                  "
                  @click-outside="displayDateInputDropdown = false"
                >
                  <DatePicker
                    :model-value="activeDate"
                    @update:model-value="handleDateInput"
                  />
                </InputDropdown>
              </template>
            </div>
          </ClientOnly>
        </div>
      </div>
      <div class="button">
        <Button
          type="secondary"
          :disabled="false"
          @click="submit"
        >
          {{ t("search.search") }}
        </Button>
      </div>
    </BngContentFit>
  </div>
</template>

<style scoped>
.c-search {
  display: flex;
  flex-direction: column;

  .lc-content-fit {
    padding: 0;
  }

  &.large-desktop {
    max-width: 100vw;
    padding: 3rem;
    margin: 0 auto;
  }

  .body {
    display: flex;
    flex-direction: column;
    padding: 1.5em 1.5em 2.75em;
    border-radius: 0 0 0.5rem 0.5rem;
    background: white;
    box-shadow: var(--box-shadow-2);

    .search-header {
      display: flex;
      flex-direction: row;
      align-items: center;

      .c-icon {
        margin-right: 1rem;
        color: var(--color-primary);
      }

      .title {
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.5rem;
        font-weight: 700;

        .colored {
          color: var(--color-primary);
        }
      }
    }

    .fields {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;

      .categories,
      .where,
      .when {
        display: flex;
        width: 100%;

        .title {
          display: none;
        }
      }

      .where,
      .when {
        .c-input, .c-input-dropdown {
          width: 100%;
          margin-bottom: 1em;

          &:deep(input) {
            &[readonly] {
              padding: 0.625em;
              background-color: var(--color-secondary-light);
            }
          }
        }
      }
    }
  }

  .button {
    position: relative;
    display: flex;
    width: 100%;
    align-self: center;
    padding: 0 1.5em;
    transform: translateY(-50%);

    .c-button {
      display: flex;
      font-size: 1.5em;
      text-transform: uppercase;
    }
  }

  @media screen and (width <= 374px) {
    .body {
      padding: 0.5em 1.5em 1.5em;
    }
  }

  @media screen and (width >= 1024px) {
    .lc-content-fit {
      padding: 0 2.5em;
    }

    .body {
      padding: 3em 4em;

      .search-header {
        .title {
          font-size: 1.75rem;
        }
      }

      .fields {
        flex-direction: row;
        justify-content: space-between;
        padding-bottom: 2.5em;

        .categories,
        .where,
        .when {
          width: 16rem;
          flex-direction: column;
          justify-content: space-between;
          padding: 1.5em 0 0.5em;
          font-size: 0.875em;

          .title {
            display: block;
            height: 2rem;
            margin-bottom: 0.5em;
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.125rem;
            font-weight: 700;
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }
        }

        .when {
          .c-input-dropdown {
            width: 100%;
            margin: auto;
          }
        }

        .where {
          position: relative;

          .c-input-dropdown {
            width: 100%;
            margin: auto;
          }

          .autocomplete-location {
            position: absolute;
            top: 0;
            overflow: auto;
            max-height: 200px;
            background-color: white;
          }
        }
      }
    }

    .button {
      position: relative;
      display: flex;
      width: 24rem;
      height: 7em;
      align-self: center;
      padding: 20px;
      border-radius: 20px;
      background: white;

      .c-button {
        font-size: 2em;
      }

      &::after {
        position: absolute;
        display: flex;
        border-radius: 0 0 20px 20px;
        box-shadow: var(--box-shadow-2);
        content: "";
        inset: 50% 0 0;
        pointer-events: none;
      }
    }
  }

  @media screen and (width >= 1280px) {
    .body {
      padding: 3em 8em;

      .search-header {
        .c-icon {
          margin-right: 1.5rem;
        }

        .title {
          font-size: 2rem;
        }
      }

      .fields {
        .categories,
        .where,
        .when {
          width: 18rem;
          font-size: 1em;
        }
      }
    }
  }

  @media screen and (width >= 1600px) {
    .body {
      padding: 3em 12em;

      .search-header {
        .title {
          font-size: 2.25rem;
        }
      }

      .fields {
        .categories,
        .where,
        .when {
          width: 20rem;
        }
      }
    }
  }
}
</style>
